import { Button, Flex, Image, ModalBody, ModalHeader, Text } from "@chakra-ui/react";
import { LivePrice } from "@prisma/client";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { ECourseAccessTier } from "../@types/course_types";
import { XOR } from "../@types/utility_types";
import PaymentsController from "../controllers/payments_controller";
import type { BaseCourse } from "../server/dao/course_dao";
import type { BaseLive } from "../server/dao/live_dao";
import { SafeSubscriptionPlan } from "../server/dao/subscription_plan_dao";
import { BaseUser } from "../server/dao/user_dao";
import { printMoney } from "../utils/currency_util";
import { isUserMemberOfCreatorCommunity, isUserSubscribedToCreator } from "../utils/user_util";
import { CheckboxList } from "./CheckboxList";
import Modal from "./Modal";
export enum EPurchaseField {
  PURCHASE_LIVE = "purchase_live",
  PURCHASE_RECORDING = "purchase_recording",
  PURCHASE_COURSE = "purchase_course",
  PURCHASE_COURSE_TIER_1 = "purchase_course_tier_1",
  PURCHASE_COURSE_TIER_2 = "purchase_course_tier_2",
  PURCHASE_SUBSCRIPTION = "purchase_subscription",
  JOIN_COMMUNITY = "join_community",
}
const BENEFITS_FOR_PURCHASE_TYPE = (purchaseType: EPurchaseField | null, creatorSlug: string, thisPurchase = "live") => [{
  active: purchaseType === EPurchaseField.PURCHASE_SUBSCRIPTION,
  show: true,
  label: "Full access to this users current and future lives"
}, {
  active: purchaseType === EPurchaseField.PURCHASE_SUBSCRIPTION,
  show: true,
  label: "Full access to all current and future recordings"
}, {
  active: purchaseType === EPurchaseField.PURCHASE_SUBSCRIPTION,
  show: purchaseType === EPurchaseField.PURCHASE_SUBSCRIPTION || purchaseType === EPurchaseField.JOIN_COMMUNITY,
  label: "Full access to community & premium channels"
}, {
  active: true,
  show: purchaseType === EPurchaseField.PURCHASE_SUBSCRIPTION,
  label: "Cancel anytime"
}, {
  active: true,
  show: purchaseType === EPurchaseField.PURCHASE_LIVE || purchaseType === EPurchaseField.PURCHASE_RECORDING,
  label: `Access ${creatorSlug}'s community`
}, {
  active: true,
  show: purchaseType === EPurchaseField.PURCHASE_LIVE || purchaseType === EPurchaseField.PURCHASE_RECORDING,
  label: `Access to this ${thisPurchase}`
}, {
  active: purchaseType === EPurchaseField.JOIN_COMMUNITY,
  show: purchaseType === EPurchaseField.JOIN_COMMUNITY,
  label: "Limited access to community"
}];
const calculateSavings = (singlePrice: number, sessions: number, subsPrice: number) => {
  return singlePrice / 100 * sessions - subsPrice / 100;
};
export interface IPurchaseOptionsModalProps {
  onSubmit: (selectedType: EPurchaseField, data: {
    live?: BaseLive;
    course?: BaseCourse;
    coursePriceId?: string;
  }) => Promise<void> | void;
  onSignIn: () => void;
  user: BaseUser;
  creator: BaseUser;
}
export interface IPurchaseOptionsModalRef {
  open: (data: {
    plan: SafeSubscriptionPlan;
    initialPurchaseField?: EPurchaseField;
  } & XOR<{
    live?: BaseLive;
  }, {
    course?: BaseCourse;
  }>) => void;
  close: (promise?: Promise<any>) => void;
}
const PurchaseOptionsModal: React.ForwardRefRenderFunction<IPurchaseOptionsModalRef, IPurchaseOptionsModalProps> = ({
  onSubmit,
  onSignIn,
  user: currentUser,
  creator
}, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [live, setLive] = useState<BaseLive | null>();
  const [course, setCourse] = useState<BaseCourse | null>();
  const [subPlan, setSubPlan] = useState<SafeSubscriptionPlan | null>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [purchaseField, setPurchaseField] = useState<EPurchaseField | null>(null);
  const creatorHasCommunity = !!creator.community;
  useImperativeHandle(ref, () => ({
    open: handleOpenModal,
    close: handleDismissModal
  }), []);
  const isViewingOwnProfile = useMemo(() => !!currentUser && currentUser?.id === creator.id!, [currentUser, creator]);
  const isCreatorOwnProfile = !!currentUser && !!currentUser?.isCreator && isViewingOwnProfile;
  const userIsSubscribed = useMemo(() => isUserSubscribedToCreator(currentUser, creator.id), [currentUser, course]);
  const liveSinglePrice = useMemo(() => {
    if (live) {
      if (live.recording) {
        return live.recording.price!.totalInLowestDenom;
      }
      return live!.price?.totalInLowestDenom;
    }
  }, [live]);
  const singlePriceStr = useMemo(() => liveSinglePrice ? printMoney({
    total: liveSinglePrice > 0 ? liveSinglePrice / 100 : 0,
    options: {
      currency: live?.price?.currency,
      roundUp: true
    }
  }) : "", [live, liveSinglePrice]);
  const subscriptionPrice = useMemo(() => printMoney({
    total: subPlan?.price.totalInLowestDenom ? subPlan?.price.totalInLowestDenom / 100 : 0,
    options: {
      currency: subPlan?.price.currency,
      roundUp: true
    }
  }) + " p/m", [subPlan]);
  const pricesAvailable = liveSinglePrice && subPlan;
  const userSavings = pricesAvailable && calculateSavings(liveSinglePrice, subPlan.sessions!, subPlan.price.totalInLowestDenom);
  const potentialSavings = useMemo(() => pricesAvailable && printMoney({
    total: userSavings ?? 0,
    options: {
      currency: subPlan?.price.currency
    }
  }), [liveSinglePrice, subPlan]);
  const isUserSaving = userSavings && userSavings >= 1;
  const getPurchaseFields = () => {
    let purchaseFields: EPurchaseField[] = [];
    if (!!course) {
      const isTiered = !!course.secondGuestCostId || !!course.secondSubscriberCostId;
      if (isTiered) {
        purchaseFields = [EPurchaseField.PURCHASE_COURSE_TIER_1, EPurchaseField.PURCHASE_COURSE_TIER_2];
      } else {
        purchaseFields = [EPurchaseField.PURCHASE_COURSE];
      }
    } else if (live) {
      const liveTypeField = live?.recording ? EPurchaseField.PURCHASE_RECORDING : EPurchaseField.PURCHASE_LIVE;
      purchaseFields = [liveTypeField];
    }
    if (subPlan) {
      purchaseFields = [...purchaseFields, EPurchaseField.PURCHASE_SUBSCRIPTION];
    }
    if (purchaseFields.length === 1 && purchaseFields[0] === EPurchaseField.PURCHASE_SUBSCRIPTION) {
      if (creatorHasCommunity) {
        purchaseFields = [...purchaseFields, EPurchaseField.JOIN_COMMUNITY];
      }
    }
    const removeFields = currentUser ? [isUserMemberOfCreatorCommunity(currentUser, creator.slug!) ? EPurchaseField.JOIN_COMMUNITY : null, isUserSubscribedToCreator(currentUser, creator.id) ? EPurchaseField.PURCHASE_SUBSCRIPTION : null].filter(Boolean) : [];
    return removeFields.length ? purchaseFields.filter(pf => removeFields.some(f => f !== pf)) : purchaseFields;
  };
  const renderCoursePrice = (price: LivePrice | null) => {
    return !!price && price.totalInLowestDenom > 0 ? printMoney({
      total: price.totalInLowestDenom > 0 ? price.totalInLowestDenom / 100 : 0,
      options: {
        currency: price!.currency,
        roundUp: true
      }
    }) : "FREE";
  };
  const checkboxOptions = useMemo(() => {
    const fields = getPurchaseFields();
    return fields.map((value: EPurchaseField) => {
      if (value === EPurchaseField.JOIN_COMMUNITY) {
        return {
          value,
          text: "Join community",
          endText: "FREE"
        };
      }
      if (value === EPurchaseField.PURCHASE_SUBSCRIPTION) {
        return {
          value,
          text: "Subscribe",
          endText: subscriptionPrice
        };
      }
      if (value === EPurchaseField.PURCHASE_LIVE) {
        return {
          value,
          text: "Join single live",
          endText: singlePriceStr
        };
      }
      if (value === EPurchaseField.PURCHASE_RECORDING) {
        return {
          value,
          text: "Watch this recording",
          endText: singlePriceStr
        };
      }
      if (value === EPurchaseField.PURCHASE_COURSE) {
        const price = userIsSubscribed ? course!.subscriberCost : course!.guestCost;
        return {
          value,
          text: "Join course",
          endText: renderCoursePrice(price)
        };
      }
      if (value === EPurchaseField.PURCHASE_COURSE_TIER_1) {
        const price = PaymentsController.getCoursePriceForUser(course!, currentUser, ECourseAccessTier.First);
        const tierName = course?.firstTierName ?? "Tier 1";
        return {
          value,
          text: `Join Course: ${tierName}`,
          endText: renderCoursePrice(price)
        };
      }
      if (value === EPurchaseField.PURCHASE_COURSE_TIER_2) {
        const price = PaymentsController.getCoursePriceForUser(course!, currentUser, ECourseAccessTier.Second);
        const tierName = course?.secondTierName ?? "Tier 2";
        return {
          value,
          text: `Join Course: ${tierName}`,
          endText: renderCoursePrice(price)
        };
      }
    }).filter(Boolean);
  }, [course, live, subPlan]);
  const handleOpenModal = (data: {
    plan: SafeSubscriptionPlan;
    initialPurchaseField?: EPurchaseField;
  } & XOR<{
    live: BaseLive;
  }, {
    course: BaseCourse;
  }>) => {
    const {
      plan,
      live,
      course,
      initialPurchaseField
    } = data;
    if (course) {
      setCourse(course);
      if (initialPurchaseField === EPurchaseField.PURCHASE_COURSE_TIER_1 || initialPurchaseField === EPurchaseField.PURCHASE_COURSE_TIER_2) {
        setPurchaseField(EPurchaseField.PURCHASE_COURSE_TIER_1);
      } else {
        setPurchaseField(EPurchaseField.PURCHASE_COURSE);
      }
    } else if (live) {
      if (live.recording) {
        setPurchaseField(EPurchaseField.PURCHASE_RECORDING);
        setLive(live);
      } else {
        setPurchaseField(EPurchaseField.PURCHASE_LIVE);
        setLive(live);
      }
    }
    if (plan) {
      setSubPlan(plan);
      setPurchaseField(initialPurchaseField ?? EPurchaseField.PURCHASE_SUBSCRIPTION);
    }
    setIsVisible(true);
  };
  const handleDismissModal = () => {
    setPurchaseField(null);
    setCourse(null);
    setLive(null);
    setSubPlan(null);
    setIsVisible(false);
  };
  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSubmitting) {
      return;
    }
    if (purchaseField === null) {
      return;
    }
    setIsSubmitting(true);
    if (purchaseField === EPurchaseField.PURCHASE_COURSE || purchaseField === EPurchaseField.PURCHASE_COURSE_TIER_1) {
      const price = PaymentsController.getCoursePriceForUser(course!, currentUser, ECourseAccessTier.First);
      await onSubmit(purchaseField, {
        course: course!,
        coursePriceId: price?.id
      });
    }
    if (purchaseField === EPurchaseField.PURCHASE_COURSE_TIER_2) {
      const price = PaymentsController.getCoursePriceForUser(course!, currentUser, ECourseAccessTier.Second);
      await onSubmit(purchaseField, {
        course: course!,
        coursePriceId: price!.id
      });
    }
    if (purchaseField === EPurchaseField.JOIN_COMMUNITY) {
      await onSubmit(purchaseField, {});
    }
    await onSubmit(purchaseField, {
      live: live!
    });
    setIsSubmitting(false);
  };
  const renderSubscriptionBenefits = () => {
    const benefits = BENEFITS_FOR_PURCHASE_TYPE(purchaseField, creator.slug!, live?.recording ? "recording" : "live");
    const benefitPurchaseType = purchaseField === EPurchaseField.PURCHASE_LIVE || purchaseField === EPurchaseField.PURCHASE_RECORDING ? benefits.reverse() : benefits;
    return benefitPurchaseType.map(({
      show,
      active,
      label
    }) => show ? <Flex flexDirection="row" key={label} my={2} justifyContent="flex-start" alignItems="flex-start">
                    <Image width="20px" src={active ? "/images/benefit-active.svg" : "/images/benefit-inactive.svg"} alt={active ? "Green tick" : "Red X"} mr={2} mt={1} />
                    <Text fontSize={16}>{label}</Text>
                </Flex> : null);
  };
  const renderUserAction = () => {
    let action: string;
    if (purchaseField === EPurchaseField.PURCHASE_LIVE) {
      action = "Pay for single live";
    } else if (purchaseField === EPurchaseField.PURCHASE_RECORDING) {
      action = "Pay for single recording";
    } else if (purchaseField === EPurchaseField.PURCHASE_SUBSCRIPTION) {
      action = "Subscribe now";
    } else if (purchaseField === EPurchaseField.PURCHASE_COURSE || purchaseField === EPurchaseField.PURCHASE_COURSE_TIER_1 || purchaseField === EPurchaseField.PURCHASE_COURSE_TIER_2) {
      action = "Pay for course";
    } else {
      action = "Join community";
    }
    return <Button onClick={handleSubmit} isLoading={isSubmitting} variant="primary" size="lg" width="100%" mb={4} boxShadow="lg" isDisabled={isCreatorOwnProfile} data-sentry-element="Button" data-sentry-component="renderUserAction" data-sentry-source-file="PurchaseOptionsModal.tsx">
                {action}
            </Button>;
  };
  const renderPurchaseOptions = () => {
    const formTitle = !!subPlan && !course && !live ? `Subscribe to ${subPlan.creator.slug}` : "Purchase options";
    const showSubSavings = !!live && !!subPlan && purchaseField === EPurchaseField.PURCHASE_SUBSCRIPTION;
    const showLiveDemerits = !!live && !!subPlan && purchaseField !== EPurchaseField.PURCHASE_SUBSCRIPTION;
    const showSubscriptionBenefits = !course;
    return <>
                <ModalHeader fontSize="xl" fontWeight="extrabold" pt={4} data-sentry-element="ModalHeader" data-sentry-source-file="PurchaseOptionsModal.tsx">
                    {formTitle}
                </ModalHeader>
                <ModalBody data-sentry-element="ModalBody" data-sentry-source-file="PurchaseOptionsModal.tsx">
                    <CheckboxList variant="buttons" name="purchaseOptions" initialValue={purchaseField}
        // @ts-ignore
        options={checkboxOptions} onChange={(nextValue: EPurchaseField) => setPurchaseField(nextValue)} data-sentry-element="CheckboxList" data-sentry-source-file="PurchaseOptionsModal.tsx" />

                    <Flex flexDirection="column" my={6} px={2} data-sentry-element="Flex" data-sentry-source-file="PurchaseOptionsModal.tsx">
                        {showSubscriptionBenefits && renderSubscriptionBenefits()}
                    </Flex>
                    {showSubSavings && <>
                            {isUserSaving && <Button colorScheme="green" size="md" width="100%" mb={4} _hover={{
            pointerEvents: "none"
          }} isDisabled={isCreatorOwnProfile}>
                                    You would save around {potentialSavings} per month
                                </Button>}
                        </>}
                    {renderUserAction()}
                    {showLiveDemerits && <>
                            {isUserSaving && <Text fontSize={14} textAlign="center" fontWeight="bold" color="blackAlpha.500" px={[2, 10, null]} pb={2}>
                                    You could save an average {potentialSavings} per month by
                                    subscribing to this creator.
                                </Text>}
                        </>}
                    {!currentUser && <Flex justifyContent="center" alignItems="center">
                            <Text fontSize="lg">Already joined?</Text>
                            <Button fontSize="lg" textDecoration="underline" padding={2} background="transparent" _hover={{
            background: "transparent"
          }} onClick={onSignIn}>
                                Sign in
                            </Button>
                        </Flex>}
                </ModalBody>
            </>;
  };
  return <Modal size="lg" onDismiss={handleDismissModal} isOpen={isVisible} canDismiss hideDismissBtn data-sentry-element="Modal" data-sentry-component="PurchaseOptionsModal" data-sentry-source-file="PurchaseOptionsModal.tsx">
            {renderPurchaseOptions()}
        </Modal>;
};
export default forwardRef(PurchaseOptionsModal);